import { Injectable, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { DeepPartial } from '../interfaces/deep-partial.type';
import { mergeDeep } from '../utils/merge-deep';
import { LayoutService } from '../services/layout.service';
import { configs } from './configs';
import { VexConfigName } from './config-name.model';
import { VexConfig } from './vex-config.interface';
import { ColorSchemeName } from './colorSchemeName';
import { CSSValue } from '../interfaces/css-value.type';
import { map } from 'rxjs/operators';
import { ColorVariable } from '../components/config-panel/color-variables';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	private document = inject<Document>(DOCUMENT);
	private layoutService = inject(LayoutService);

	defaultConfig: VexConfigName = VexConfigName.poseidon;
	configs: VexConfig[] = configs;

	private _configSubject = new BehaviorSubject(this.configs.find((c) => c.id === this.defaultConfig));
	config$ = this._configSubject.asObservable();

	constructor() {
		this.config$.subscribe((config) => this._updateConfig(config));
	}

	select<R>(selector: (config: VexConfig) => R): Observable<R> {
		return this.config$.pipe(map(selector));
	}

	setConfig(config: VexConfigName) {
		const settings = this.configs.find((c) => c.id === config);
		this._configSubject.next(settings);
	}

	updateConfig(config: DeepPartial<VexConfig>) {
		this._configSubject.next(mergeDeep({ ...this._configSubject.getValue() }, config));
	}

	private _updateConfig(config: VexConfig): void {
		this._setLayoutClass(config.id);
		this._setStyle(config.style);
		this._setDirection(config.direction);
		this._setSidenavState(config.sidenav.state);
		this._emitResize();
	}

	private _setStyle(style: VexConfig['style']): void {
		/**
		 * Color Scheme
		 */
		Object.values(ColorSchemeName)
			.filter((s) => s !== style.colorScheme)
			.forEach((value) => {
				if (this.document.body.classList.contains(value)) {
					this.document.body.classList.remove(value);
				}
			});

		this.document.body.classList.add(style.colorScheme);

		/**
		 * Border Radius
		 */
		this.document.body.style.setProperty('--border-radius', `${style.borderRadius.value}${style.borderRadius.unit}`);

		const buttonBorderRadius: CSSValue = style.button.borderRadius ?? style.borderRadius;
		this.document.body.style.setProperty('--button-border-radius', `${buttonBorderRadius.value}${buttonBorderRadius.unit}`);

		/**
		 * Primary Color
		 */
		if (style.colors.primary) {
			this.document.body.style.setProperty('--color-primary', style.colors.primary.default.replace('rgb(', '').replace(')', ''));
			this.document.body.style.setProperty('--color-primary-rgb', style.colors.primary.default);
			this.document.body.style.setProperty('--color-primary-contrast', style.colors.primary.contrast.replace('rgb(', '').replace(')', ''));
			this.document.body.style.setProperty('--color-primary-contrast-rgb', style.colors.primary.contrast);
		}
	}

	/**
	 * Emit event so charts and other external libraries know they have to resize on layout switch
	 * @private
	 */
	private _emitResize(): void {
		if (window) {
			window.dispatchEvent(new Event('resize'));
			setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
		}
	}

	private _setDirection(direction: 'ltr' | 'rtl') {
		this.document.body.dir = direction;
	}

	private _setSidenavState(sidenavState: 'expanded' | 'collapsed'): void {
		sidenavState === 'expanded' ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();
	}

	private _setLayoutClass(layout: VexConfigName): void {
		this.configs.forEach((c) => {
			if (this.document.documentElement.classList.contains(c.id)) {
				this.document.documentElement.classList.remove(c.id);
			}
		});

		this.document.documentElement.classList.add(layout);
	}

	changeColorByCustomer(color: ColorVariable) {
		if (this.document) {
			this.document.documentElement.style.setProperty('--color-primary', color.default.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-rgb', color.default);
			this.document.documentElement.style.setProperty('--color-accent', color.accent.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-accent-rgb', color.accent);
			this.document.documentElement.style.setProperty('--color-warn', color.warn.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-warn-rgb', color.warn);
			this.document.documentElement.style.setProperty('--color-danger', color.danger.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-danger-rgb', color.danger);
			this.document.documentElement.style.setProperty('--color-primary-contrast', color.contrast.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-contrast-rgb', color.contrast);
		}
	}

	changeImage(logoBlob: string) {
		this._configSubject.value.imgSrc = logoBlob;
		this._configSubject.value.sidenav.imageUrl = logoBlob;
		this._configSubject.next(this._configSubject.getValue());
	}

	changeFavIcon(favIconBlob: string) {
		const favIcon: any = document.querySelector('#appIcon');
		favIcon.href = favIconBlob;
		this._configSubject.next(this._configSubject.getValue());
	}
}
